import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { KeyboardArrowDown } from '@mui/icons-material'
import CustomInput from '../Returns/CustomInput'
import CustomSelect from '../Returns/CustomSelect'
import CustomMultiSelect from '../Returns/CustomMultiSelect'
import StyledSwitch from '../common/StyledSwitch'

const AssignEntityForm = ({
  assignEntityRows,
  entities,
  activeUsers,
  tanList,
  formErrors,
  formLoading,
  editingAssignment,
  onRowChange,
  onSubmit,
}) => {
  // Get available TANs based on selected entities for a row
  const getAvailableTANs = selectedEntityNames => {
    if (!selectedEntityNames || selectedEntityNames.length === 0) {
      return []
    }
    if (!tanList || tanList.length === 0) {
      return []
    }

    // Try to match by entity UUID first, then fall back to name matching
    const selectedEntities = entities.filter(entity =>
      selectedEntityNames.includes(entity.name)
    )

    const availableTANs = tanList
      .filter(tanItem => {
        // Try UUID matching first
        const uuidMatch = selectedEntities.some(
          entity =>
            entity.uuid === tanItem.entityUuid || entity.uuid === tanItem.uuid
        )

        // Fall back to name matching
        const nameMatch = selectedEntityNames.includes(tanItem.entityName)

        return uuidMatch || nameMatch
      })
      .filter(tanItem => tanItem.active)
      .map(tanItem => tanItem.tan)

    // Remove duplicates
    return [...new Set(availableTANs)]
  }

  return (
    <Box>
      {/* Mandatory fields note */}
      <Typography
        variant="body2"
        sx={{
          color: 'var(--forbrand-error)',
          mb: 3,
          fontSize: '14px',
        }}
      >
        (*) Fields are mandatory
      </Typography>

      {/* Form container */}
      <Box component="form" onSubmit={onSubmit}>
        {/* Assign Entity Form -  Rows */}
        {assignEntityRows.map((row, index) => {
          return (
            <Box key={row.id} sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                {/* Row 1: User ID and User Name */}
                <Grid item size={6}>
                  <CustomSelect
                    label="User ID"
                    required
                    fullWidth
                    placeholder="Select User ID"
                    value={row.userId || ''}
                    onChange={e => onRowChange(index, 'userId', e.target.value)}
                    options={activeUsers}
                    getOptionLabel={user => user.userId}
                    getOptionValue={user => user.userId}
                    error={!!formErrors[`userId_${index}`]}
                    helperText={formErrors[`userId_${index}`]}
                  />
                </Grid>

                <Grid item size={6}>
                  <CustomInput
                    label="User Name"
                    placeholder="User Name"
                    value={row.username}
                    disabled
                  />
                </Grid>

                {/* Row 2: Email ID and Business Role */}
                <Grid item size={6}>
                  <CustomInput
                    label="Email ID"
                    placeholder="Email ID"
                    type="email"
                    value={row.email}
                    disabled
                  />
                </Grid>

                <Grid item size={6}>
                  <CustomInput
                    label="Business Role"
                    placeholder="Business Role"
                    value={
                      Array.isArray(row.businessRole)
                        ? row.businessRole.join(', ')
                        : row.businessRole || ''
                    }
                    disabled
                  />
                </Grid>

                {/* Row 3: Entity Names (Multi-select with checkboxes) */}
                {!editingAssignment && (
                  <Grid item size={6}>
                    <CustomMultiSelect
                      label="Entity Name"
                      fullWidth
                      required
                      placeholder="Select Entity Name"
                      value={row.entityNames || []}
                      onChange={e =>
                        onRowChange(index, 'entityNames', e.target.value)
                      }
                      options={entities}
                      getOptionLabel={entity => entity.name}
                      getOptionValue={entity => entity.name}
                      error={!!formErrors[`entityNames_${index}`]}
                      helperText={formErrors[`entityNames_${index}`]}
                    />
                  </Grid>
                )}

                {/* Row 4: Entity TANs (Multi-select with checkboxes) */}
                {!editingAssignment && (
                  <Grid item size={6}>
                    <CustomMultiSelect
                      label="Entity TAN"
                      fullWidth
                      required
                      placeholder="Select Entity TAN"
                      value={row.entityTANs || []}
                      onChange={e =>
                        onRowChange(index, 'entityTANs', e.target.value)
                      }
                      options={getAvailableTANs(row.entityNames).map(tan => tan)}
                      disabled={
                        !row.entityNames ||
                        row.entityNames.length === 0 ||
                        getAvailableTANs(row.entityNames).length === 0
                      }
                      error={!!formErrors[`entityTANs_${index}`]}
                      helperText={formErrors[`entityTANs_${index}`]}
                    />
                  </Grid>
                )}

                {/* SSO toggle */}
                {editingAssignment && (
                  <Grid
                    item
                    size={6}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: '16px',
                        color: '#333333',
                        fontWeight: 500,
                      }}
                    >
                      SSO
                    </Typography>
                    <StyledSwitch
                      checked={!!row.ssoUser}
                      onChange={e =>
                        onRowChange(index, 'ssoUser', e.target.checked)
                      }
                    />
                  </Grid>
                )}

                {/* Error messages */}
                {formErrors[`duplicate_${index}`] && (
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: '#d32f2f' }}>
                      {formErrors[`duplicate_${index}`]}
                    </Typography>
                  </Grid>
                )}
                {formErrors[`sameUser_${index}`] && (
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: '#d32f2f' }}>
                      {formErrors[`sameUser_${index}`]}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              {formErrors.general && (
                <Typography variant="body2" color="error" sx={{ my: 2 }}>
                  {formErrors.general}
                </Typography>
              )}
            </Box>
          )
        })}
      </Box>

      {/* Submit button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Button
          type="submit"
          variant="contained"
          disabled={formLoading}
          onClick={onSubmit}
          sx={{
            backgroundColor: 'var( --forbrand-bg-variant)',
            color: 'var(--forbrand-brand-varient)',
            textTransform: 'none',
            px: '16px',
            py: '8px',
            fontSize: '16px',
            fontWeight: 500,
            borderRadius: '4px',
            '&:disabled': {
              backgroundColor: '#ccc',
              boxShadow: 'none',
            },
          }}
        >
          {formLoading ? 'Saving...' : editingAssignment ? 'Update' : 'Save'}
        </Button>
      </Box>
    </Box>
  )
}

export default AssignEntityForm
